import React from 'react';
import Search from './search/Search';
import { ToolNames } from './ToolNames';
import MapContent from './mapContent/MapContent';
import AvailableTopicLayers from './mapContent/topics/AvailableTopicLayers';
import TopicLayerSettings from './mapContent/topics/TopicLayerSettings';
import useAppState, { useTemporalStore } from '../AppStore';
import Configurations from './configuration/Configurations';
import Draw from './draw/Draw';
import DrawSettings from './draw/settings/DrawSettings';
import Print from './print/Print';
import Share from './share/Share';
import Reset from './reset/Reset';
import { FlyinNames } from '../UIStateSlice';
import { useTracking } from '../utils/tracking/TrackingContext';
import TopicLayerAttributeSearch from './mapContent/topics/attributeSearch/TopicLayerAttributeSearch';
import Help from './help/Help';
import ReleaseNotes from './help/ReleaseNotes';
import { MapContentViews } from './MapContentViews';

export interface ToolProps {
  label: string;
  icon: string;
  addSeparator?: boolean;
  views?: ToolWindowProps[];
  badge?: string;
  disabled?: boolean;
  tooltip?: string;
  onClick?: () => void;
}

export interface ToolWindowProps {
  key: string;
  label: string;
  isMainView: boolean;
  displayInDrawer?: boolean;
  viewComponent?: React.ReactNode;
}

const useMapContentTool = () => {
  const counterTopicLayers = useAppState((state) => state.selectedTopicLayers);
  const badgeLayersToolItem =
    counterTopicLayers.length > 0 ? counterTopicLayers.length.toString() : undefined;

  return {
    label: 'mapContent_tool_label',
    icon: 'layers',
    badge: badgeLayersToolItem,
    views: [
      {
        key: MapContentViews.main,
        label: 'mapContent_tool_label',
        isMainView: true,
        viewComponent: <MapContent />,
      } as ToolWindowProps,
      {
        key: MapContentViews.addTopic,
        label: 'add_topic',
        isMainView: false,
        viewComponent: <AvailableTopicLayers />,
      } as ToolWindowProps,
      {
        key: MapContentViews.topicSettings,
        label: 'topic_settings',
        isMainView: false,
        viewComponent: <TopicLayerSettings />,
      } as ToolWindowProps,
      {
        key: MapContentViews.topicAttributeSearch,
        label: 'topic_attribute_search',
        isMainView: false,
        displayInDrawer: true,
        viewComponent: <TopicLayerAttributeSearch />,
      } as ToolWindowProps,
    ],
  } as ToolProps;
};

const useIdentifyTool = () => {
  const activeTool = useAppState((state) => state.activeTool);
  const setActiveFlyin = useAppState((state) => state.setActiveFlyin);
  const isIdentifyEnabled = useAppState((state) => state.enabledIdentify);
  const toggleIdentify = useAppState((state) => state.toggleIdentify);

  return {
    label: 'identify_tool_label',
    icon: 'hand-point',
    onClick: () => {
      if (isIdentifyEnabled) {
        setActiveFlyin(null);
      } else {
        setActiveFlyin(FlyinNames.Identify);
      }

      if (![ToolNames.draw, ToolNames.print].includes(activeTool)) toggleIdentify();
    },
  } as ToolProps;
};

const useUndoTool = () => {
  const { pastStates, undo } = useTemporalStore();
  const { Tracking } = useTracking();
  return {
    label: 'undo_tool_label',
    icon: 'arrow-left',
    disabled: !pastStates.length,
    onClick: () => {
      Tracking.UI.undo();
      undo();
    },
  } as ToolProps;
};

const useHelpTool = () => {
  const { Tracking } = useTracking();
  return {
    label: 'help_tool_label',
    icon: 'questionmark-circle',
    onClick: () => {
      Tracking.UI.openHelp();
    },
    views: [
      {
        key: 'help_tool',
        label: 'help_tool_label',
        isMainView: true,
        viewComponent: <Help />,
      } as ToolWindowProps,
      {
        key: 'help_tool_relase_notes',
        label: 'help_tool_relase_notes',
        isMainView: false,
        viewComponent: <ReleaseNotes />,
      } as ToolWindowProps,
    ],
  };
};

const useConfiguration = (): ToolProps => {
  return {
    label: 'configuraton_tool_label',
    icon: 'save',
    views: [
      {
        key: 'configuration_tool',
        label: 'configuraton_tool_label',
        isMainView: true,
        viewComponent: <Configurations />,
      } as ToolWindowProps,
    ],
  };
};

export const useTools = () => {
  const drawFeatures = useAppState((state) => state.drawFeatures);
  const drawBadge = drawFeatures.length > 0 ? drawFeatures.length.toString() : undefined;

  const tools: Record<ToolNames, ToolProps> = {
    [ToolNames.search]: {
      label: 'search_tool_label',
      icon: 'loupe',
      views: [
        {
          key: 'search_tool',
          label: 'search_tool_label',
          isMainView: true,
          viewComponent: <Search />,
        } as ToolWindowProps,
      ],
    },
    [ToolNames.layerSelection]: useMapContentTool(),
    [ToolNames.identify]: useIdentifyTool(),
    [ToolNames.draw]: {
      label: 'draw_tool_label',
      icon: 'pencil-line',
      badge: drawBadge,
      views: [
        {
          key: 'draw_tool',
          label: 'draw_tool_label',
          isMainView: true,
          displayInDrawer: true,
          viewComponent: <Draw />,
        } as ToolWindowProps,
        {
          key: 'draw_tool_settings',
          label: 'draw_tool_settings_label',
          isMainView: false,
          displayInDrawer: true,
          viewComponent: <DrawSettings />,
        } as ToolWindowProps,
      ],
    },
    [ToolNames.print]: {
      label: 'print_tool_label',
      icon: 'printer',
      addSeparator: true,
      views: [
        {
          key: 'print_tool',
          label: 'print_tool_label',
          isMainView: true,
          displayInDrawer: true,
          viewComponent: <Print />,
        } as ToolWindowProps,
      ],
    },
    [ToolNames.save]: useConfiguration(),
    [ToolNames.share]: {
      label: 'share_tool_label',
      addSeparator: true,
      icon: 'share',
      views: [
        {
          key: 'share_tool',
          label: 'share_tool_label',
          isMainView: true,
          displayInDrawer: true,
          viewComponent: <Share />,
        } as ToolWindowProps,
      ],
    },
    [ToolNames.undo]: useUndoTool(),
    [ToolNames.reset]: {
      label: 'reset_tool_label',
      icon: 'restart',
      addSeparator: true,
      views: [
        {
          key: 'reset_tool',
          label: 'reset_tool_label',
          isMainView: true,
          viewComponent: <Reset />,
        } as ToolWindowProps,
      ],
    },
    [ToolNames.help]: useHelpTool(),
  };

  return tools;
};

export const DEFAULT_TOOL: ToolNames = ToolNames.identify;
